/**
 * The layout builders every page uses, and the one module that knows the measure rules.
 *
 * WHY THIS EXISTS. Webwin's measure pass is opt-in PER AXIS: a control reports the size it already
 * has unless `autoWidth` / `autoHeight` hands that axis to its parent. Get one link of a chain wrong
 * and the symptom is not an error - it is a stack stuck at its 120px constructor default, or a
 * wrapped paragraph clipped to one line. The chain a CRM page needs is four links deep:
 *
 *     Page (dock fill)
 *       -> ScrollViewer (dock fill)            sized by the page
 *         -> StackPanel (autoHeight, stretch)  measured tall, stretched wide; its HEIGHT is the
 *                                              scroll range, because ScrollContent.extent() is the
 *                                              union of its children's bounds
 *           -> Card (autoHeight, stretch)      Panel.preferredSize = content + padding
 *             -> CrmLabel (wrap, autoHeight)   measured from the wrapped text at the given width
 *
 * Writing those flags at each of a thousand call sites is how three of them come to be wrong, so the
 * builders here set them and pages do not mention them. The rule worth remembering: `scrollPage`,
 * `vstack`, `card` and `text` all measure themselves, and anything Webwin does NOT measure -
 * ScrollViewer, ListBox, ListView, DataGrid, TextArea, TabControl - must be given `dock: 'fill'` or
 * a `fill` weight instead, never an auto height. `fillWith` is the helper for that.
 */
import {
  Button,
  Container,
  CheckBox,
  ComboBox,
  insets,
  Panel,
  ScrollViewer,
  StackPanel,
  TextBox,
  WrapPanel,
  type Alignment,
  type ButtonOptions,
  type CheckBoxOptions,
  type ComboBoxOptions,
  type Control,
  type FontSpec,
  type Insets,
  type LayoutPanel,
  type LayoutPanelOptions,
  type PanelOptions,
  type ScrollViewerOptions,
  type Size,
  type StackPlacement,
  type TextBoxOptions,
  type WrapPanelOptions
} from 'webwin';
import { Card, type CardOptions } from './Card.js';
import { CrmScroller } from './Scroller.js';
import { CrmLabel, type CrmLabelOptions, type TextRole } from './Text.js';

/** The font family the theme uses. Duplicated from CrmTheme because a FontSpec needs it by name. */
const UI_FONT = "'Segoe UI Variable Text', 'Segoe UI', system-ui, -apple-system, sans-serif";

/** The type scale, matching the OpenSilver H1 / H2 / body / caption styles. */
export const FONT = {
  h1: { family: UI_FONT, size: 20, weight: 700 } satisfies FontSpec,
  h2: { family: UI_FONT, size: 15, weight: 600 } satisfies FontSpec,
  bold: { family: UI_FONT, size: 13, weight: 600 } satisfies FontSpec,
  body: { family: UI_FONT, size: 13 } satisfies FontSpec,
  small: { family: UI_FONT, size: 12 } satisfies FontSpec,
  tiny: { family: UI_FONT, size: 11 } satisfies FontSpec,
  /** A KPI figure. */
  figure: { family: UI_FONT, size: 22, weight: 700 } satisfies FontSpec
} as const;

/** The spacing scale. One set of numbers, so two cards cannot disagree about a gutter. */
export const GAP = { xs: 2, sm: 6, md: 10, lg: 16, xl: 24 } as const;

/** The width a page's content is held to, so a table does not stretch to a 4K monitor's width. */
export const PAGE_MAX_WIDTH = 960;

/** Below this the shell collapses to the drawer layout. The OpenSilver threshold, unchanged. */
export const NARROW_WIDTH = 760;

// ---------------------------------------------------------------------
//  Stacks
// ---------------------------------------------------------------------

/**
 * A vertical stack that grows with its children and stretches across its parent.
 *
 * `autoSize` and `autoHeight` are both set, and they are not redundant: `autoHeight` lets a parent
 * that measures (a Container's alignment pass, another stack) ask how tall this wants to be, while
 * `autoSize` makes the stack resize ITSELF after laying out - which is what keeps a scroll range
 * correct when a child changes and nobody re-measures from above.
 */
export function vstack(options: LayoutPanelOptions = {}): StackPanel {
  return new StackPanel({
    orientation: 'vertical',
    spacing: GAP.md,
    align: 'stretch',
    autoSize: true,
    autoHeight: true,
    hAlign: 'stretch',
    ...options
  });
}

/**
 * A horizontal row that grows with its children.
 *
 * `align: 'center'` rather than the stretch a column gets: a row is usually a label beside a button,
 * and stretching both to the tallest makes a 30px button 30px of centred text instead.
 */
export function hstack(options: LayoutPanelOptions = {}): StackPanel {
  return new StackPanel({
    orientation: 'horizontal',
    spacing: GAP.sm,
    align: 'center',
    autoSize: true,
    autoWidth: true,
    autoHeight: true,
    ...options
  });
}

/**
 * A row of buttons that wraps rather than overflowing.
 *
 * `hstack` measures to its content and will happily report 381px inside a 375px phone, which on a
 * canvas means the last button is simply not on screen - there is no document scrollbar to reveal
 * it. A WrapPanel breaks the line instead, which is the fix for the one thing CLAUDE.md still lists
 * as squeezing on a phone in the OpenSilver client: per-row button clusters.
 *
 * `autoHeight` with `hAlign: 'stretch'` is the combination that makes it work - the panel needs the
 * host's width to know where to break, and has to report the height the breaking produced.
 */
export function buttonRow(options: WrapPanelOptions = {}): WrapPanel {
  return new WrapPanel({
    orientation: 'horizontal',
    spacing: GAP.sm,
    lineSpacing: GAP.sm,
    align: 'center',
    autoSize: true,
    autoHeight: true,
    hAlign: 'stretch',
    ...options
  });
}

/**
 * A heading line: a title with chips, ids or dates beside it, which breaks onto a second line rather
 * than running off the card.
 *
 * Built as a plain `hstack` these rows measured to their content and, on a phone, were wider than the
 * column they sat in - the chip after a long ticket title, the date after an author's name, drawn
 * past the card's edge and clipped. A wrap panel moves what does not fit to the next line, and a
 * `value` too long for a whole line wraps inside it.
 */
export function inlineRow(options: WrapPanelOptions = {}): WrapPanel {
  return buttonRow({ lineSpacing: GAP.xs, ...options });
}

/**
 * A row whose first child grows and whose last hugs its content: a title on the left, actions on the
 * right.
 *
 * **NOT `hstack`, and the difference is a real bug rather than a preference.** `hstack` sets
 * `autoSize`, which for a horizontal StackPanel means "set my width to my content's width" - and a
 * child that has volunteered for a share of the leftovers measures as ZERO, because a stack being
 * asked how small it could be gets no opinion from a child that absorbs slack. So the row shrinks to
 * the sum of its auto-sized children, the parent stretches it back out, and the arrange that
 * distributed the leftovers is stale: the "filling" child keeps its natural width and the right-hand
 * group sits immediately beside it instead of at the far edge. Measured on the device page's header,
 * where seven buttons and a machine name ended up jammed together in the middle of a 740px row.
 *
 * `autoHeight` stays on, so the row still reports the height its content needs; only the main axis
 * has to stop sizing itself.
 */
/**
 * A horizontal stack that measures a filling child's height at the width it is actually given.
 *
 * WEBWIN'S STACK MEASURES EVERY CHILD'S HEIGHT AT THE ROW'S FULL WIDTH, then hands the filling child
 * only what the others leave. Text that wraps in the narrower share was therefore sized as if it
 * fitted on fewer lines, and the extra lines were clipped: a card's dates cut off mid-sentence at
 * phone width, a section's description losing its last line beside two buttons. Nothing errored.
 *
 * After the normal pass, each filling child is measured again at its real width - in `measure`, so
 * the row asks its parent for the right height, and in `arrange`, so the child is given it.
 * (The real fix belongs in Webwin's `StackPanel`; this holds until it lands there.)
 */
export interface SplitRowOptions extends LayoutPanelOptions {
  /**
   * Below this width for the filling child, the row stacks its children vertically instead - the
   * text first at full width, then what was beside it. 0 never stacks.
   */
  stackBelow?: number;
}

/** The share under which a card's text column gives up the row and the buttons move underneath. */
export const STACK_BELOW = 150;

export class SplitRow extends StackPanel {
  readonly stackBelow: number;
  // Not a #private field: the base constructor lays out before a subclass's private fields exist.
  private stacked = false;

  constructor(options: SplitRowOptions = {}) {
    super(options);
    this.stackBelow = options.stackBelow ?? 0;
  }

  /**
   * Whether `available` is too narrow to keep the filling child beside the rest.
   *
   * A title column beside three buttons on a phone was left 102px - a word a line, its chips cut at
   * the edge. Stacking hands the text the whole width and puts the buttons on the line below.
   */
  #shouldStack(available: Size): boolean {
    if (!(this.stackBelow > 0)) return false;
    const children = this.laidOutChildren();
    const was = this.stacked;
    this.stacked = false;
    try {
      const shares = this.#fillShares(available);
      if (shares.length === 0 || shares.length === children.length) return false;
      return shares.some(s => s.width < this.stackBelow);
    } finally {
      this.stacked = was;
    }
  }

  #setStacked(stacked: boolean): void {
    this.stacked = stacked;
    this.orientation = stacked ? 'vertical' : 'horizontal';
  }

  override placementOf(control: Control): StackPlacement {
    // Stacked, a weight would mean "share the leftover HEIGHT" - and measure as nothing.
    return this.stacked ? {} : super.placementOf(control);
  }

  protected override crossAlignOf(c: Control): Alignment {
    if (!this.stacked) return super.crossAlignOf(c);
    return (super.placementOf(c).fill ?? 0) > 0 ? 'stretch' : 'start';
  }

  /** The filling children and the width each is given out of `available`. */
  #fillShares(available: Size): Array<{ child: Control; width: number }> {
    const children = this.laidOutChildren();
    const fills = children.filter(c => (this.placementOf(c).fill ?? 0) > 0);
    if (fills.length === 0) return [];
    let taken = this.spacing * Math.max(0, children.length - 1);
    for (const c of children) {
      const m = this.marginOf(c);
      taken += m.mainBefore + m.mainAfter;
      if (!fills.includes(c)) taken += this.naturalSize(c, available).width;
    }
    const spare = Math.max(0, available.width - taken);
    const total = fills.reduce((sum, c) => sum + (this.placementOf(c).fill ?? 0), 0);
    return fills.map(child => ({ child, width: (spare * (this.placementOf(child).fill ?? 0)) / total }));
  }

  /** The child's height at `width`, for a child that measures its own height; null otherwise. */
  #heightAt(child: Control, width: number, height: number): number | null {
    return child.autoHeight ? child.preferredSize({ width, height }).height : null;
  }

  protected override measure(available: Size): Size {
    // The base constructor lays out once before this class's private members exist.
    if (this.stackBelow === undefined) return super.measure(available);
    this.#setStacked(this.#shouldStack(available));
    const size = super.measure(available);
    if (this.stacked) return size;
    let height = size.height;
    for (const { child, width } of this.#fillShares(available)) {
      const h = this.#heightAt(child, width, available.height);
      if (h === null) continue;
      const m = this.marginOf(child);
      height = Math.max(height, h + m.crossBefore + m.crossAfter);
    }
    return { width: size.width, height };
  }

  protected override arrange(available: Size): Size {
    // The base constructor lays out once before this class's private members exist.
    if (this.stackBelow === undefined) return super.arrange(available);
    this.#setStacked(this.#shouldStack(available));
    const size = super.arrange(available);
    if (this.stacked) return size;
    let height = size.height;
    for (const child of this.laidOutChildren()) {
      if ((this.placementOf(child).fill ?? 0) <= 0) continue;
      const measured = this.#heightAt(child, child.width, available.height);
      if (measured === null) continue;
      // Always re-placed, not only when taller than it is: an auto-sizing stack grows itself to its
      // true height as soon as it is given its width, but keeps the offset the first pass computed
      // for its shorter height - centred too low, with its last line hanging off the row.
      const h = Math.max(measured, child.height);
      const m = this.marginOf(child);
      const cross = this.crossPlace(available.height, h, m.crossBefore, m.crossAfter, this.crossAlignOf(child));
      child.setBounds(child.x, cross.offset, child.width, cross.size);
      height = Math.max(height, cross.offset + cross.size + m.crossAfter);
    }
    return { width: size.width, height };
  }
}

export function splitRow(options: SplitRowOptions = {}): StackPanel {
  return new SplitRow({
    stackBelow: STACK_BELOW,
    orientation: 'horizontal',
    spacing: GAP.md,
    align: 'center',
    autoSize: false,
    autoWidth: false,
    autoHeight: true,
    hAlign: 'stretch',
    ...options
  });
}

/**
 * Gives a child a weighted share of the leftover space on its stack's main axis - the `*` column of
 * a Grid, for a stack. This is how a row gets a title on the left and buttons on the right.
 *
 * The stack must not size itself on that axis - see `splitRow`.
 */
export function fillWith<T extends Control>(stack: StackPanel, child: T, weight = 1): T {
  stack.controls.add(child);
  stack.setPlacement(child, { fill: weight });
  return child;
}

/**
 * Lays a control out and then every ancestor, innermost first.
 *
 * A page's height is a chain of auto-sized panels ending at a `ScrollViewer`, whose scroll range is
 * `ScrollContent.extent()` - the union of its children's bounds, read live. So the range is only ever
 * as correct as the height of the stack inside it, and `panel.layout()` lays out a panel's CHILDREN:
 * it resizes that panel, and tells its parent nothing.
 *
 * The consequence is the bug this exists to prevent. A page that finishes loading, fills four card
 * lists and calls `layout()` on its own root has a root of the right height inside a content stack
 * still holding the height it had when the page was empty - so the scroller clamps to a range of a
 * hundred-odd pixels and everything below the fold is UNREACHABLE. Nothing errors, the tree is
 * correct, and the missing sections are simply not scrollable to.
 *
 * Walking to the top is O(depth) - about six panels - and only happens when data changed, so the
 * cheap correct thing wins over remembering which ancestor to nudge at each of forty call sites.
 */
export function relayout(control: Control | null | undefined): void {
  let node: Control | null | undefined = control;
  while (node) {
    if (node instanceof Container) node.layout();
    node = node.parent;
  }
  // Once more where it started. An ancestor's pass can resize the starting panel - narrow a panel
  // that was just shown, say - and the re-arrange that resize should trigger is swallowed, because
  // Webwin blocks a layout re-entering one already in progress. Measured on the Reports page: the
  // shown sections panel narrowed to 649 while its cards stayed 663 and ran off the right edge.
  if (control instanceof Container) control.layout();
}

/**
 * Adds children in order and returns the panel, so a layout reads as one expression.
 *
 * Generic over the panel rather than typed to `StackPanel`, because a wrapping button row is a
 * `WrapPanel` and reads exactly the same way at the call site.
 */
export function stackOf<T extends LayoutPanel>(stack: T, ...children: Control[]): T {
  for (const child of children) stack.controls.add(child);
  return stack;
}

/** Fixed empty space, for the rare gap the stack's own spacing cannot express. */
export function spacer(size = GAP.md): Panel {
  return new Panel({ width: size, height: size, clickThrough: true, style: 'none' });
}

/** Space that absorbs whatever is left, pushing what follows to the far end of a row. */
export function flexSpacer(stack: StackPanel): Panel {
  const panel = new Panel({ width: 0, height: 0, clickThrough: true, style: 'none' });
  return fillWith(stack, panel);
}

// ---------------------------------------------------------------------
//  Text
// ---------------------------------------------------------------------

/**
 * Body text.
 *
 * Wraps and measures by default, which is the opposite of Webwin's `Label` and deliberate: every
 * string in this app comes from a customer's data or a server's sentence, so the one that overflows
 * is the one nobody tested. `text(s, { wrap: false })` opts out for a value in a fixed-width column.
 */
export function text(content: string, options: CrmLabelOptions = {}): CrmLabel {
  return new CrmLabel({
    text: content,
    wrap: true,
    autoHeight: true,
    hAlign: 'stretch',
    font: FONT.body,
    ...options
  });
}

/** A page or section title. */
export function h1(content: string): CrmLabel {
  return text(content, { font: FONT.h1 });
}

/** A card's heading. */
export function h2(content: string): CrmLabel {
  return text(content, { font: FONT.h2 });
}

/** A supporting line under a heading. */
export function muted(content: string, role: TextRole = 'muted'): CrmLabel {
  return text(content, { font: FONT.small, role });
}

/** The caption above a field. Wraps, like all text here: a long one on a phone loses nothing. */
export function caption(content: string): CrmLabel {
  return text(content, { font: FONT.small, role: 'muted' });
}

/**
 * One value on a row, measured to its own width rather than stretched.
 *
 * `hAlign: 'start'` matters: without it a value in a horizontal stack is stretched to the tallest
 * cross extent and a right-aligned number drifts away from its column.
 */
export function value(content: string, options: CrmLabelOptions = {}): CrmLabel {
  return new CrmLabel({
    text: content,
    // Wraps rather than ellipsizing when the room runs out: as wide as its text when it fits, see
    // `CrmLabel.preferredSize`. `wrap: false` opts back into one line with an ellipsis.
    wrap: true,
    autoWidth: true,
    autoHeight: true,
    ellipsis: true,
    font: FONT.body,
    ...options
  });
}

// ---------------------------------------------------------------------
//  Surfaces
// ---------------------------------------------------------------------

/** A card, measured from its contents. */
export function card(options: CardOptions = {}): Card {
  return new Card({ autoHeight: true, hAlign: 'stretch', ...options });
}

/**
 * A page: a scroller filling the shell's content area, with a width-capped column inside it.
 *
 * Returns the parts rather than a single control because a caller needs two of them - `content` to
 * add to, and `view` to reach the scroll position or switch horizontal scrolling on. The money pages
 * do the latter: their fixed-column tables cannot reflow to a phone, so they pan as a sheet.
 *
 * `maxWidth` is applied by padding the content stack rather than by centring a fixed-width child,
 * because a centred child with a fixed width cannot also stretch on a narrow screen.
 */
export function scrollPage(options: { padding?: Insets; scroll?: ScrollViewerOptions } = {}): {
  view: CrmScroller;
  content: StackPanel;
} {
  const view = new CrmScroller({
    dock: 'fill',
    verticalScroll: 'auto',
    // Card-list pages reflow and must never scroll sideways; a page that needs it says so.
    horizontalScroll: 'never',
    showBorder: false,
    zoomEnabled: false,
    ...options.scroll
  });

  const content = vstack({
    padding: options.padding ?? insets(GAP.xl, GAP.xl, GAP.xl, GAP.xl * 2),
    spacing: GAP.lg
  });

  view.content.controls.add(content);
  return { view, content };
}

// ---------------------------------------------------------------------
//  Controls, with the CRM's defaults
// ---------------------------------------------------------------------

/**
 * A button.
 *
 * `isDefault` is what makes it brand blue: the theme's accent fill is reached through that flag,
 * because Webwin's Button has no colour of its own. So a primary action is `button(t, fn, true)` and
 * everything else is the bordered card-coloured default.
 */
export function button(
  label: string,
  onClick: () => void,
  primary = false,
  options: ButtonOptions = {}
): Button {
  const b = new Button({
    text: label,
    isDefault: primary,
    autoWidth: true,
    height: 30,
    cursor: 'pointer',
    ...options
  });
  b.on('click', () => onClick());
  return b;
}

/** A text field. */
export function textBox(options: TextBoxOptions = {}): TextBox {
  return new TextBox({ height: 30, hAlign: 'stretch', ...options });
}

/**
 * A password field.
 *
 * The whole of what the OpenSilver app needed `PasswordBox` plus two visual-tree walks plus a
 * `PasswordProvider` lambda for: `passwordChar` masks it and `.text` reads it, because nothing here
 * has to be bindable.
 */
export function passwordBox(options: TextBoxOptions = {}): TextBox {
  return textBox({ passwordChar: '•', ...options });
}

export function checkBox(label: string, checked = false, options: CheckBoxOptions = {}): CheckBox {
  return new CheckBox({ text: label, checked, autoWidth: true, autoHeight: true, ...options });
}

const checkLines = new WeakMap<CheckBox, StackPanel>();

/**
 * A check box whose caption WRAPS.
 *
 * Webwin's toggle draws its caption on one line and ellipsizes the rest, which at phone width cut
 * "Open a ticket from mail sent by a known customer contact" down to "Open a ticket from mail sent by
 * a known c…". This is a caption-less box beside a wrapping label; a click on the label toggles the box
 * the way a click on a native caption does. Returns the box, so callers read `.checked` and listen for
 * `change` exactly as before - put `checkLine(box)` in the layout, not the box itself.
 */
export function wrappingCheck(label: string, checked = false): CheckBox {
  const box = new CheckBox({ checked, autoWidth: true, autoHeight: true });
  const row = splitRow({ align: 'start', spacing: GAP.sm, cursor: 'pointer' });
  row.controls.add(box);
  fillWith(row, text(label));
  // On the row, not the label: a label takes no hits, so a click on the caption lands on the panel
  // behind it. A click on the box itself is the box's own and does not reach here.
  row.on('click', () => {
    if (!box.effectivelyEnabled) return;
    box.focus();
    box.checked = !box.checked;
  });
  checkLines.set(box, row);
  return box;
}

/** The row a `wrappingCheck` lives in - what goes into a stack. */
export function checkLine(box: CheckBox): Control {
  return checkLines.get(box) ?? box;
}

export function comboBox<T>(items: readonly T[], options: ComboBoxOptions<T> = {}): ComboBox<T> {
  return new ComboBox<T>({ items, height: 30, hAlign: 'stretch', ...options });
}

/** A captioned field: the label-over-control pair every form in this app is made of. */
export function field(label: string, control: Control, options: LayoutPanelOptions = {}): StackPanel {
  return stackOf(vstack({ spacing: GAP.xs, ...options }), caption(label), control);
}

/**
 * Holds a control to at most `max` wide, centred, with a gutter on a narrow screen.
 *
 * The canvas equivalent of `MaxWidth` plus `Margin`, which Webwin has no property for: a control's
 * `width` is a number, and `hAlign: 'center'` centres whatever that number is - so a 360px card on a
 * 375px phone leaves 7px either side, and a 340px one leaves the card clipped at 320. Recomputed on
 * every resize of the host, which is what makes it survive a rotation.
 */
export function capWidth(host: Container, control: Control, max: number, gutter = GAP.lg): void {
  const apply = (): void => {
    const available = host.clientSize.width;
    if (available <= 0) return;
    control.setSize(Math.max(0, Math.min(max, available - gutter * 2)), control.height);
  };
  apply();
  host.on('resize', apply);
}

/** Two fields side by side, each taking half - the shape the customer and device editors use. */
export function fieldRow(...fields: Control[]): StackPanel {
  const row = splitRow({ spacing: GAP.md, align: 'start' });
  for (const f of fields) fillWith(row, f);
  return row;
}

export { Card } from './Card.js';
export { CrmLabel } from './Text.js';
export type { TextRole } from './Text.js';
